import { cva, VariantProps } from 'class-variance-authority';
import { Paperclip } from 'lucide-react';
import React, { useMemo } from 'react';
import { ErrorCode, DropzoneOptions, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { cn } from '@/utils';

import { Markdown } from '../Markdown';
import { FileItem } from './FileItem';
import { convertMime } from './helpers';
import { TFunction } from 'i18next';
import { FileDto } from '@/features/shared/dtos/file';

const UPLOAD_ERRORS = {
	[ErrorCode.FileTooLarge]: (maxFileSize, accept, t: TFunction) => t('v.fileTooLarge', {size: maxFileSize / 1024 / 1024}),
	[ErrorCode.FileTooSmall]: (maxFileSize, accept, t: TFunction) => t('v.fileTooSmall'),
	[ErrorCode.FileInvalidType]: (maxFileSize, accept, t: TFunction) => t('v.fileFormat', {f: accept.toLowerCase().replaceAll('.', '')})
}

const dropzoneVariants = cva(
	'transition-colors flex flex-wrap items-center rounded-lg px-4 border-brand-gray-3 border border-dashed',
	{
		variants: {
			size: {
				sm: 'max-w-64 py-2',
				md: 'w-full py-4',
				lg: 'w-full py-4 min-h-52 text-center',
			},
		},
		defaultVariants: {
			size: 'md',
		},
	}
);

const iconVariants = cva('w-auto flex justify-center', {
	variants: {
		size: {
			sm: '',
			md: '',
			lg: 'w-full',
		},
	},
	defaultVariants: {
		size: 'md',
	},
});

export interface UploadProps extends VariantProps<typeof dropzoneVariants>, DropzoneOptions {
	readonly?: boolean;
	name?: string;
	label?: string;
	description?: string;
	icon?: boolean;
	className?: string;
	fileSize?: number;
	files?: File[];
	onRemove?: (file: File) => void;
}

const FileRejectionItems = ({t, fileRejections, maxFileSize, accept}) => {
	return (
		<div className={cn('w-full px-4 pt-2 text-center text-xs text-red-500')}>
			<ul>
				{fileRejections.map(({ file, errors }) =>
					<li key={file.path}>
						<span className={cn('font-bold')}>{file.path.replace('/', '')}</span>:
						<ol>
							{errors.map(e => (
								<li key={e.code}>{UPLOAD_ERRORS[e.code](maxFileSize, accept, t) || e.message}</li>
							))}
						</ol>
					</li>
				)}
			</ul>
		</div>
	);
}

const Dropzone = ({
	size,
	label,
	className,
	description,
	icon = true,
	multiple,
	readonly,
	name,
	maxSize,
	...options
}: UploadProps) => {
	const { t } = useTranslation();
	if (!maxSize) {
		maxSize = undefined;
	}
	const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({ multiple, maxSize, ...options });

	const format = useMemo(() => {
		if (Array.isArray(options.accept)) {
			return convertMime(options.accept);
		}

		return convertMime(options.accept?.split(','));
	}, [options.accept]);

	return (
		<div
			{...(!readonly ? getRootProps() : {})}
			className={cn(
				dropzoneVariants({ size }),
				{ 'border-brand-blue bg-brand-blue-light/10': isDragActive },
				{ 'cursor-not-allowed opacity-50': options.disabled },

				className
			)}
		>
			{!readonly && <input {...getInputProps()} name={name} />}

			<div
				className={cn('flex gap-x-2.5 flex-wrap transition-opacity select-none', {
					'opacity-20': isDragActive,
				})}
			>
				{icon && (
					<div className={iconVariants({ size })}>
						<Paperclip width={16} />
					</div>
				)}
				<div className="flex flex-col gap-2 flex-1">
					<div>{label ?? t(`common.uploadFile${multiple ? 's' : ''}`)}</div>
					{description && (
						<div className="text-xs">
							<Markdown>{description}</Markdown>
						</div>
					)}
				</div>
			</div>
			{fileRejections.length > 0 && <FileRejectionItems t={t} fileRejections={fileRejections} maxFileSize={maxSize} accept={format} />}
		</div>
	);
};

const Alt = ({
	size,
	label,
	className,
	description,
	icon = true,
	multiple,
	fileSize,
	files = [],
	onRemove,
	readonly,
	name,
	maxSize,
	...options
}: UploadProps) => {
	const { t } = useTranslation();
	if (!maxSize) {
		maxSize = undefined;
	}
	const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({ multiple, maxSize, ...options });

	const format = useMemo(() => {
		if (Array.isArray(options.accept)) {
			return convertMime(options.accept);
		}

		return convertMime(options.accept?.split(','));
	}, [options.accept]);

	return (
		<>
			<div
				{...(!readonly ? getRootProps() : {})}
				className={cn(
					dropzoneVariants({ size }),
					{ 'border-brand-blue bg-brand-blue-light/10': isDragActive },
					className
				)}
			>
				{!readonly && <input {...getInputProps()} name={name} />}

				<div
					className={cn('flex gap-2.5 flex-col flex-1 transition-opacity items-center', {
						'opacity-20': isDragActive,
					})}
				>
					<div className="flex gap-2 text-base">
						{icon && (
							<div className={iconVariants({ size })}>
								<Paperclip width={24} />
							</div>
						)}
						{label ?? (
							<>
								<a href="#" className="underline hover:no-underline text-blue-600">
									{t('common.uploadFile')}
								</a>{' '}
								{t('common.uploadDrag')}
							</>
						)}
					</div>

					<p className="text-xs">{t('v.fileFormatAndSize', { format, size: '5Mb' })}</p>
				</div>
			</div>

			{files?.map((file) => {
				return <FileItem key={file.name} file={file} onRemove={onRemove ? () => onRemove?.(file) : undefined} />;
			})}
			{fileRejections.length > 0 && <FileRejectionItems t={t} fileRejections={fileRejections} maxFileSize={maxSize} accept={format} />}
		</>
	);
};

Dropzone.Alt = Alt;

export { Dropzone };
